/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { createSelector } from 'reselect';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import sortBy from 'lodash/sortBy';
import omit from 'lodash/omit';

import {
  GET_DASHBOARD_PINS,
  GET_DASHBOARD_PINS_SUCCESS,
  GET_DASHBOARD_PINS_FAILURE,
  ADD_DASHBOARD_PIN,
  ADD_DASHBOARD_PIN_SUCCESS,
  ADD_DASHBOARD_PIN_FAILURE,
  DELETE_DASHBOARD_PIN,
  DELETE_DASHBOARD_PIN_SUCCESS,
  DELETE_DASHBOARD_PIN_FAILURE,
  UPDATE_DASHBOARD_PIN,
  UPDATE_DASHBOARD_PIN_SUCCESS,
  UPDATE_DASHBOARD_PIN_FAILURE,
  CAPTURING_PIN_THUMBNAIL,
} from 'dashboardPinActionCreators';

// Simple selector will be memoized later
const getPins = state => get(state, 'dashboardPins.data');

// Because we're processing state, need to memoize it
export const getDashboardPins =
  createSelector([getPins], pins => Object.values(sortBy(pins, 'default'))) || [];

export const dashboardPinsReducer = (
  state = { data: {}, updatingData: false, fetchingData: false },
  action
) => {
  switch (action.type) {
    case GET_DASHBOARD_PINS:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
        updateDataError: null,
      };
    case GET_DASHBOARD_PINS_FAILURE:
      return {
        ...state,
        updatingData: false,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case GET_DASHBOARD_PINS_SUCCESS:
      return {
        ...state,
        updatingData: false,
        fetchingData: false,
        fetchDataError: null,
        data: keyBy(action.payload, 'dashboardPinId'),
      };
    case DELETE_DASHBOARD_PIN_SUCCESS:
      return {
        ...state,
        updatingData: false,
        // remove the deleted summary from the state
        data: omit(state.data, [action.payload.dashboardPinId]),
      };
    case ADD_DASHBOARD_PIN_SUCCESS:
    case UPDATE_DASHBOARD_PIN_SUCCESS:
      return {
        ...state,
        updatingData: false,
        fetchingData: false,
        fetchDataError: null,
        data: {
          ...state.data,
          [action.payload.dashboardPinId]: action.payload,
        },
      };
    case CAPTURING_PIN_THUMBNAIL:
    case UPDATE_DASHBOARD_PIN:
    case ADD_DASHBOARD_PIN:
    case DELETE_DASHBOARD_PIN:
      return {
        ...state,
        updatingData: action.type,
        updateDataError: null,
      };
    case ADD_DASHBOARD_PIN_FAILURE:
    case DELETE_DASHBOARD_PIN_FAILURE:
    case UPDATE_DASHBOARD_PIN_FAILURE:
      return {
        ...state,
        updatingData: false,
        updateDataError: action.payload,
      };
    default:
      return state;
  }
};
